import React, { useState } from 'react';
import { 
  Zap, Cpu, Sun, Layers, Download, Copy, Check, ArrowLeft, 
  Sparkles, FileText, RefreshCw, FileCheck, ShieldCheck, 
  Activity, Sliders, BatteryCharging, Gauge, CheckCircle2,
  BellRing, AlertTriangle
} from 'lucide-react';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';

interface CircuitSchematicAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
}

type CircuitType = 'psu' | 'alarm' | 'board' | 'solar' | 'switch' | 'ne555';

export default function CircuitSchematicAgentView({
  language,
  onBack,
  onGoHome
}: CircuitSchematicAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const [circuitType, setCircuitType] = useState<CircuitType>('psu');
  
  // PSU state
  const [psuVout, setPsuVout] = useState<string>('');
  const [psuIout, setPsuIout] = useState<string>('');
  const [psuVsec, setPsuVsec] = useState<string>('');

  // Alarm state
  const [alarmPirCount, setAlarmPirCount] = useState<string>('3');
  const [alarmSirenMinutes, setAlarmSirenMinutes] = useState<string>('5');
  const [alarmPhone, setAlarmPhone] = useState<string>('+380501234567');

  // Power board state (Однолінійна схема)
  const [boardKw, setBoardKw] = useState<string>('');
  const [boardPhases, setBoardPhases] = useState<string>('3');

  // Solar BESS state
  const [solarPvKw, setSolarPvKw] = useState<string>('');
  const [solarInvKw, setSolarInvKw] = useState<string>('');
  const [solarBatKwh, setSolarBatKwh] = useState<string>('');

  // Switch state
  const [switchVload, setSwitchVload] = useState<string>('');
  const [switchIload, setSwitchIload] = useState<string>('');
  const [switchType, setSwitchType] = useState<string>('N-MOSFET');

  // NE555 state
  const [ne555Freq, setNe555Freq] = useState<string>('');
  const [ne555Duty, setNe555Duty] = useState<string>('60');

  const [projectNotes, setProjectNotes] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [resultData, setResultData] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [showSignModal, setShowSignModal] = useState<boolean>(false);

  // 1-Click Demo Presets for 3 Languages
  const applyDemoPreset = () => {
    if (circuitType === 'psu') {
      setPsuVout('12.0');
      setPsuIout('1.5');
      setPsuVsec('15.0');
      setProjectNotes(
        isUk ? 'Лінійний стабілізований блок живлення 12V 1.5A з індикацією, фільтрацією та захистом.' :
        isPl ? 'Liniowy zasilacz stabilizowany do automatyki i czujników 12V 1.5A ze wskaźnikiem LED i zabezpieczeniem.' :
        'Linear regulated power supply for industrial sensors and automation 12V 1.5A with LED indicator and fuse protection.'
      );
    } else if (circuitType === 'alarm') {
      setAlarmPirCount('3');
      setAlarmSirenMinutes('5');
      setAlarmPhone('+380501234567');
      setProjectNotes(
        isUk ? 'Охоронна сигналізація дачного будинку: 3х PIR датчики руху, геркон вхідних дверей, таймер сирени NE555 рівно на 5 хв (300 сек), GSM-модуль SIM800L для автододзвону та АКБ 12V 7Ah (до 65 год резерву).' :
        isPl ? 'System alarmowy domku letniskowego: 3x czujniki ruchu PIR, kontaktron drzwiowy, syrena na 5 min (układ NE555), moduł GSM SIM800L i akumulator buforowy 12V 7Ah.' :
        'Country house security alarm: 3x PIR motion sensors, door reed switch, 5-minute siren NE555 timer, SIM800L GSM auto-dialer, and 12V 7Ah backup battery.'
      );
    } else if (circuitType === 'board') {
      setBoardKw('15.0');
      setBoardPhases('3');
      setProjectNotes(
        isUk ? 'Ввідно-розподільний щит (ВРП) трифазного живлення приватного будинку 15 кВт з реле напруги, ПЗІП та дифзахистом за ПУЕ-2017.' :
        isPl ? 'Rozdzielnica główna nN dla domu jednorodzinnego 15 kW (3-fazy 400V) z ochronnikiem SPD, przekaźnikami napięciowymi i RCD.' :
        'Main power distribution board (MDB) 15 kW 3-phase 400V with voltage monitoring relay, SPD Class II and selective RCD per IEC 60364.'
      );
    } else if (circuitType === 'solar') {
      setSolarPvKw('12.0');
      setSolarInvKw('10.0');
      setSolarBatKwh('15.0');
      setProjectNotes(
        isUk ? 'Гібридна сонячна електростанція СЕС 12 кВт з гібридним інвертором 10 кВт та LiFePO4 акумуляторним накопичувачем 15 кВт·год (51.2V).' :
        isPl ? 'Hybrydowa instalacja fotowoltaiczna 12 kW z falownikiem 10 kW i magazynem energii LiFePO4 15 kWh (BESS 51.2V).' :
        'Hybrid solar PV system 12 kW with 10 kW hybrid inverter and 15 kWh LiFePO4 BESS storage battery (51.2V pack).'
      );
    } else if (circuitType === 'switch') {
      setSwitchVload('12.0');
      setSwitchIload('2.0');
      setSwitchType('N-MOSFET');
      setProjectNotes(
        isUk ? 'Ключ комутації навантаження 12V 2A (реле/соленоїд/LED) з керуванням від 3.3V GPIO мікроконтролера ESP32.' :
        isPl ? 'Układ klucza tranzystorowego MOSFET do sterowania przekaźnikiem 12V 2A z poziomu mikrokontrolera ESP32 (3.3V GPIO).' :
        'Logic-level N-MOSFET load switch for 12V 2A inductive relay/solenoid driven by 3.3V MCU GPIO.'
      );
    } else if (circuitType === 'ne555') {
      setNe555Freq('1000');
      setNe555Duty('60');
      setProjectNotes(
        isUk ? 'Астабільний генератор прямокутних тактових імпульсів частотою 1 кГц зі шпаруватістю 60% для тактування логічних схем.' :
        isPl ? 'Generator astabilny przebiegu prostokątnego 1 kHz z wypełnieniem 60% na układzie scalonym NE555.' :
        'Astable square wave clock pulse generator 1 kHz with 60% duty cycle built with precision NE555 timer.'
      );
    }
  };

  const handleGenerate = async () => {
    setIsProcessing(true);
    setErrorMessage(null);
    setResultData(null);

    const payload: any = {
      circuit_type: circuitType,
      language
    };

    if (circuitType === 'psu') {
      payload.v_out = parseFloat(psuVout) || 5.0;
      payload.i_out = parseFloat(psuIout) || 1.0;
      payload.v_sec = parseFloat(psuVsec) || 12.0;
    } else if (circuitType === 'alarm') {
      payload.pir_count = parseInt(alarmPirCount) || 3;
      payload.siren_minutes = parseFloat(alarmSirenMinutes) || 5.0;
      payload.phone = alarmPhone;
    } else if (circuitType === 'board') {
      payload.power_kw = parseFloat(boardKw) || 15.0;
      payload.phases = parseInt(boardPhases) || 3;
    } else if (circuitType === 'solar') {
      payload.pv_kw = parseFloat(solarPvKw) || 10.0;
      payload.inv_kw = parseFloat(solarInvKw) || 8.0;
      payload.bat_kwh = parseFloat(solarBatKwh) || 15.0;
    } else if (circuitType === 'switch') {
      payload.v_load = parseFloat(switchVload) || 12.0;
      payload.i_load = parseFloat(switchIload) || 1.5;
      payload.switch_type = switchType;
    } else if (circuitType === 'ne555') {
      payload.freq_hz = parseFloat(ne555Freq) || 1000.0;
      payload.duty = parseFloat(ne555Duty) || 60.0;
    }

    try {
      const res = await fetch('/api/circuit/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `Помилка сервера HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Невідома помилка генерації');
      }

      setResultData(data);
    } catch (err: any) {
      console.error('Circuit generation error:', err);
      setErrorMessage(err.message || 'Сталася помилка при розрахунку схеми');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadBase64File = (base64Data: string, filename: string, mimeType: string) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isSingleLine = circuitType === 'board';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/80 border border-slate-800 p-6 rounded-2xl backdrop-blur-xl shadow-2xl">
          <div className="flex items-center gap-4">
            <div className="p-3.5 bg-gradient-to-tr from-amber-500 to-yellow-400 rounded-xl text-slate-950 shadow-lg shadow-amber-500/20">
              <Zap className="w-8 h-8" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  {isUk ? '⚡ ІНЖЕНЕРІЯ СХЕМ & BOM' : isPl ? '⚡ INŻYNIERIA SCHEMATÓW & BOM' : '⚡ ELECTRICAL CAD & BOM'}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                  isSingleLine 
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' 
                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                }`}>
                  {isSingleLine 
                    ? (isUk ? '📋 Однолінійна схема (Електропостачання)' : isPl ? '📋 Schemat jednokreskowy (Zasilanie)' : '📋 Single-Line Diagram (Power Grid)')
                    : (isUk ? '🔌 Принципова електрична схема (За замовчуванням)' : isPl ? '🔌 Schemat ideowy (Domyślny)' : '🔌 Full Schematic Diagram (Default)')
                  }
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  100% ДСТУ / IEC 60617
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white via-slate-200 to-amber-200 bg-clip-text text-transparent mt-1.5">
                {isUk ? 'Інженер Електричних Схем, Розрахунків Номіналів & BOM' :
                 isPl ? 'Inżynier Schematów Elektrycznych, Obliczeń i Zestawień BOM' :
                 'Electrical & Electronic Circuit Designer, Rating Calculator & BOM'}
              </h1>
              <p className="text-slate-400 text-sm mt-0.5">
                {isUk ? 'Принципові електричні схеми за замовчуванням (електроніка, сигналізації, таймери, СЕС) та однолінійні схеми для силового електропостачання' :
                 isPl ? 'Domyślne schematy ideowe (elektronika, alarmy, timery, fotowoltaika) oraz schematy jednokreskowe dla rozdzielnic nN' :
                 'Full schematic diagrams by default (electronics, alarms, timers, solar BESS) and single-line diagrams strictly for power distribution'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-200 rounded-xl transition border border-slate-700 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              {isUk ? 'Назад до Центру Енергетики' : isPl ? 'Powrót do Centrum Energetyki' : 'Back to Energy Hub'}
            </button>
            {onGoHome && (
              <button
                onClick={onGoHome}
                className="px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-300 rounded-xl transition border border-slate-700 text-sm"
                title="На головну"
              >
                🏠
              </button>
            )}
          </div>
        </div>

        {/* Circuit Type Selection Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { id: 'psu', icon: Cpu, typeBadge: isUk ? 'Принципова' : 'Schematic', labelUk: 'Блок Живлення AC-DC', labelPl: 'Zasilacz AC-DC', labelEn: 'AC-DC Power Supply', desc: 'LM78xx / Bridge / Filter' },
            { id: 'alarm', icon: BellRing, typeBadge: isUk ? 'Принципова' : 'Schematic', labelUk: 'Охоронна Сигналізація', labelPl: 'System Alarmowy', labelEn: 'Security Alarm', desc: '3x PIR, Двері, GSM, 5 хв' },
            { id: 'solar', icon: Sun, typeBadge: isUk ? 'Принципова' : 'Schematic', labelUk: 'СЕС + LiFePO4 BESS', labelPl: 'Fotowoltaika + BESS', labelEn: 'Solar PV + Battery', desc: 'DC Захист, Інвертор' },
            { id: 'switch', icon: Activity, typeBadge: isUk ? 'Принципова' : 'Schematic', labelUk: 'MOSFET / Реле Ключ', labelPl: 'Klucz MOSFET / BJT', labelEn: 'Transistor Switch', desc: 'Комутація навантаження' },
            { id: 'ne555', icon: Gauge, typeBadge: isUk ? 'Принципова' : 'Schematic', labelUk: 'Генератор NE555', labelPl: 'Generator NE555', labelEn: 'NE555 Timer Generator', desc: 'Тактові імпульси / ШІМ' },
            { id: 'board', icon: Layers, typeBadge: isUk ? 'Однолінійна' : 'Single-Line', labelUk: 'Щит Електропостачання', labelPl: 'Rozdzielnica nN', labelEn: 'Power Distribution', desc: 'ВРП/ГРЩ за ПУЕ (1Ф/3Ф)' }
          ].map((item) => {
            const IconComp = item.icon;
            const active = circuitType === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setCircuitType(item.id as CircuitType);
                  setResultData(null);
                }}
                className={`flex flex-col items-start p-3.5 rounded-xl border transition-all text-left ${
                  active 
                    ? 'bg-amber-500/10 border-amber-500/60 shadow-lg shadow-amber-500/10 text-white' 
                    : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
                }`}
              >
                <div className="flex items-center justify-between w-full mb-2">
                  <div className={`p-2 rounded-lg ${active ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300'}`}>
                    <IconComp className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${
                    item.id === 'board' 
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                      : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  }`}>
                    {item.typeBadge}
                  </span>
                </div>
                <div className="font-semibold text-xs text-slate-100 line-clamp-1">
                  {isUk ? item.labelUk : isPl ? item.labelPl : item.labelEn}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{item.desc}</div>
              </button>
            );
          })}
        </div>

        {/* Input Parameters Form Card */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl backdrop-blur-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 mb-5 border-b border-slate-800 gap-3">
            <div className="flex items-center gap-2.5">
              <Sliders className="w-5 h-5 text-amber-400" />
              <div>
                <h2 className="text-lg font-bold text-white">
                  {isUk ? 'Параметри проектування та розрахунку' : isPl ? 'Parametry projektowe i obliczeniowe' : 'Design & Sizing Parameters'}
                </h2>
                <div className="text-xs text-slate-400 mt-0.5">
                  {isSingleLine
                    ? (isUk ? '⚠️ Обрано режим: ОДНОЛІНІЙНА СХЕМА (для силового електропостачання та щитів ВРП)' : 'Single-Line Mode for Power Distribution')
                    : (isUk ? '✅ Обрано режим: ПРИНЦИПОВА ЕЛЕКТРИЧНА СХЕМА (повне розведення контактів, мікросхем, ключів та живлення)' : 'Full Schematic Diagram Mode (Default)')
                  }
                </div>
              </div>
            </div>
            <button
              onClick={applyDemoPreset}
              className="flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 hover:from-amber-500/30 hover:to-yellow-500/30 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-semibold transition"
            >
              <Sparkles className="w-4 h-4" />
              {isUk ? '⚡ Заповнити демо-прикладом у 1 клік' : isPl ? '⚡ Wypełnij przykładem demo' : '⚡ 1-Click Demo Preset'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {circuitType === 'psu' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Вихідна напруга DC (В)' : isPl ? 'Napięcie wyjściowe DC (V)' : 'Output Voltage DC (V)'}
                  </label>
                  <input
                    type="number"
                    value={psuVout}
                    onChange={(e) => setPsuVout(e.target.value)}
                    placeholder="Напр. 5.0, 12.0, 24.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Струм навантаження (А)' : isPl ? 'Prąd obciążenia (A)' : 'Load Current (A)'}
                  </label>
                  <input
                    type="number"
                    value={psuIout}
                    onChange={(e) => setPsuIout(e.target.value)}
                    placeholder="Напр. 1.0, 1.5, 2.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Вторинна напруга трансформатора AC (В)' : isPl ? 'Napięcie wtórne transformatora AC (V)' : 'Transformer Secondary AC (V)'}
                  </label>
                  <input
                    type="number"
                    value={psuVsec}
                    onChange={(e) => setPsuVsec(e.target.value)}
                    placeholder="Напр. 9.0, 12.0, 15.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
              </>
            )}

            {circuitType === 'alarm' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Кількість PIR датчиків руху' : isPl ? 'Liczba czujników ruchu PIR' : 'PIR Motion Sensors Count'}
                  </label>
                  <input
                    type="number"
                    value={alarmPirCount}
                    onChange={(e) => setAlarmPirCount(e.target.value)}
                    placeholder="Напр. 3, 4, 6"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Тривалість сирени (хвилин)' : isPl ? 'Czas trwania syreny (minut)' : 'Siren Alarm Duration (minutes)'}
                  </label>
                  <input
                    type="number"
                    value={alarmSirenMinutes}
                    onChange={(e) => setAlarmSirenMinutes(e.target.value)}
                    placeholder="5 (300 секунд)"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Номер для автододзвону GSM' : isPl ? 'Numer telefonu powiadomień GSM' : 'GSM Dialing Mobile Phone'}
                  </label>
                  <input
                    type="text"
                    value={alarmPhone}
                    onChange={(e) => setAlarmPhone(e.target.value)}
                    placeholder="+380..."
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
              </>
            )}

            {circuitType === 'board' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Розрахункова потужність (кВт)' : isPl ? 'Moc obliczeniowa (kW)' : 'Design Power Load (kW)'}
                  </label>
                  <input
                    type="number"
                    value={boardKw}
                    onChange={(e) => setBoardKw(e.target.value)}
                    placeholder="Напр. 10.0, 15.0, 25.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Кількість фаз введення' : isPl ? 'Liczba faz zasilania' : 'Input Phases Count'}
                  </label>
                  <select
                    value={boardPhases}
                    onChange={(e) => setBoardPhases(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-amber-500 text-sm"
                  >
                    <option value="3">{isUk ? '3 фази (~400V 50Hz)' : isPl ? '3 fazy (400V 50Hz)' : '3-Phase (~400V 50Hz)'}</option>
                    <option value="1">{isUk ? '1 фаза (~230V 50Hz)' : isPl ? '1 faza (230V 50Hz)' : '1-Phase (~230V 50Hz)'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Тип креслення' : isPl ? 'Typ schematu' : 'Drawing Type'}
                  </label>
                  <input
                    type="text"
                    disabled
                    value="Однолінійна схема (Single-Line за ПУЕ)"
                    className="w-full px-3.5 py-2.5 bg-blue-950/40 border border-blue-800/40 rounded-xl text-blue-300 text-sm font-medium"
                  />
                </div>
              </>
            )}

            {circuitType === 'solar' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Потужність сонячних панелей СЕС (кВт)' : isPl ? 'Moc paneli fotowoltaicznych (kW)' : 'Solar PV Array Power (kW)'}
                  </label>
                  <input
                    type="number"
                    value={solarPvKw}
                    onChange={(e) => setSolarPvKw(e.target.value)}
                    placeholder="Напр. 10.0, 15.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Потужність інвертора (кВт)' : isPl ? 'Moc falownika hybrydowego (kW)' : 'Inverter Power Rating (kW)'}
                  </label>
                  <input
                    type="number"
                    value={solarInvKw}
                    onChange={(e) => setSolarInvKw(e.target.value)}
                    placeholder="Напр. 8.0, 12.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Ємність LiFePO4 акумулятора (кВт·год)' : isPl ? 'Pojemność baterii LiFePO4 (kWh)' : 'LiFePO4 BESS Battery Capacity (kWh)'}
                  </label>
                  <input
                    type="number"
                    value={solarBatKwh}
                    onChange={(e) => setSolarBatKwh(e.target.value)}
                    placeholder="Напр. 10.0, 15.0, 20.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
              </>
            )}

            {circuitType === 'switch' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Напруга навантаження (В)' : isPl ? 'Napięcie obciążenia (V)' : 'Load Supply Voltage (V)'}
                  </label>
                  <input
                    type="number"
                    value={switchVload}
                    onChange={(e) => setSwitchVload(e.target.value)}
                    placeholder="Напр. 12.0, 24.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Струм навантаження (А)' : isPl ? 'Prąd obciążenia (A)' : 'Load Current (A)'}
                  </label>
                  <input
                    type="number"
                    value={switchIload}
                    onChange={(e) => setSwitchIload(e.target.value)}
                    placeholder="Напр. 1.0, 3.0"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Тип транзисторного ключа' : isPl ? 'Typ tranzystora' : 'Transistor Topology'}
                  </label>
                  <select
                    value={switchType}
                    onChange={(e) => setSwitchType(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-amber-500 text-sm"
                  >
                    <option value="N-MOSFET">N-Channel Logic-Level MOSFET</option>
                    <option value="NPN-BJT">NPN Bipolar (2N2222 / BD139)</option>
                  </select>
                </div>
              </>
            )}

            {circuitType === 'ne555' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Бажана частота імпульсів (Гц)' : isPl ? 'Częstotliwość impulsów (Hz)' : 'Target Pulse Frequency (Hz)'}
                  </label>
                  <input
                    type="number"
                    value={ne555Freq}
                    onChange={(e) => setNe555Freq(e.target.value)}
                    placeholder="Напр. 1000, 2500, 10000"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Шпаруватість / Duty Cycle (%)' : isPl ? 'Współczynnik wypełnienia (%)' : 'Duty Cycle (%)'}
                  </label>
                  <input
                    type="number"
                    value={ne555Duty}
                    onChange={(e) => setNe555Duty(e.target.value)}
                    placeholder="Напр. 60, 75"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    {isUk ? 'Режим роботи таймера' : isPl ? 'Tryb pracy NE555' : 'Operating Mode'}
                  </label>
                  <input
                    type="text"
                    disabled
                    value="Astable Multivibrator (Генератор)"
                    className="w-full px-3.5 py-2.5 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-400 text-sm"
                  />
                </div>
              </>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-xs font-medium text-slate-300 mb-1.5">
              {isUk ? 'Додатковий інженерний опис завдання / вимоги ТЗ' : isPl ? 'Dodatkowe wymagania projektowe / opis' : 'Engineering Specifications & Special Requirements'}
            </label>
            <textarea
              rows={2}
              value={projectNotes}
              onChange={(e) => setProjectNotes(e.target.value)}
              placeholder={isUk ? 'Вкажіть особливості умов монтажу, вимоги до захисту, типи роз\'ємів...' : 'Enter installation details, connector constraints, ambient conditions...'}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm"
            />
          </div>

          <div className="mt-5 flex items-center justify-end">
            <button
              onClick={handleGenerate}
              disabled={isProcessing}
              className="flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 font-bold rounded-xl shadow-lg shadow-amber-500/25 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {isUk ? 'Проведення розрахунків & Рендеринг...' : isPl ? 'Obliczanie & Renderowanie...' : 'Calculating Ratings & Rendering...'}
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  {isUk ? '🚀 Розрахувати Номінали, Згенерувати Схему & BOM' :
                   isPl ? '🚀 Oblicz Wartości, Wygeneruj Schemat & BOM' :
                   '🚀 Sizing Calculation, Render Schematic & Generate BOM'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error notification */}
        {errorMessage && (
          <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-sm flex items-center gap-3">
            <span className="text-xl">⚠️</span>
            <div>{errorMessage}</div>
          </div>
        )}

        {/* Generated Output Showcase */}
        {resultData && (
          <div className="space-y-6">
            {/* Visual Schematic Diagram Box */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 mb-4 border-b border-slate-800 gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-500/10 text-amber-400 rounded-lg">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {isSingleLine 
                        ? (isUk ? '1. Однолінійна Схема Електропостачання (ПУЕ)' : '1. Single-Line Power Distribution Diagram')
                        : (isUk ? '1. Принципова Електрична Схема' : '1. Schematic Diagram (Full Wiring)')
                      }
                    </h3>
                    <div className="text-xs text-slate-400">
                      {resultData.title}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {resultData.image_base64 && (
                    <button
                      onClick={() => downloadBase64File(resultData.image_base64, `${resultData.circuit_type}_schematic.png`, 'image/png')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-medium transition"
                    >
                      <Download className="w-4 h-4 text-amber-400" />
                      {isUk ? 'Завантажити схему (PNG)' : isPl ? 'Pobierz schemat (PNG)' : 'Download Schematic (PNG)'}
                    </button>
                  )}
                </div>
              </div>

              {/* Rendered Image Preview */}
              {resultData.image_base64 ? (
                <div className="bg-white p-4 rounded-xl border border-slate-700 flex items-center justify-center overflow-auto max-h-[550px] shadow-inner">
                  <img
                    src={`data:image/png;base64,${resultData.image_base64}`}
                    alt="Schematic Diagram"
                    className="max-w-full h-auto object-contain rounded"
                  />
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500 text-sm">
                  {isUk ? 'Зображення схеми формується...' : 'Rendering schematic...'}
                </div>
              )}
            </div>

            {/* Calculated Engineering Metrics Breakdown */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
              <div className="flex items-center gap-2.5 mb-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-bold text-white">
                  {isUk ? '2. Результати інженерних розрахунків та підбору елементів' : isPl ? '2. Wyniki obliczeń inżynieryjnych i dobór elementów' : '2. Engineering Calculations & Component Sizing'}
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {resultData.calc && Object.entries(resultData.calc).map(([k, v]: [string, any]) => (
                  <div key={k} className="p-3.5 bg-slate-950/70 border border-slate-800/80 rounded-xl">
                    <div className="text-xs text-slate-400 font-mono capitalize">{k.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-bold text-amber-300 mt-1">{String(v)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bill of Materials (BOM) Table */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 mb-4 border-b border-slate-800 gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-500/10 text-blue-400 rounded-lg">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {isUk ? '3. Специфікація матеріалів та обладнання (BOM)' : isPl ? '3. Zestawienie Materiałowe i Komponenty (BOM)' : '3. Bill of Materials (BOM Specification)'}
                    </h3>
                    <div className="text-xs text-slate-400">
                      {isUk ? `Всього позицій: ${resultData.bom?.length || 0} шт.` : `Total items: ${resultData.bom?.length || 0}`}
                    </div>
                  </div>
                </div>

                {/* Direct 1-Click Export Actions */}
                <div className="flex flex-wrap items-center gap-2">
                  {resultData.excel_base64 && (
                    <button
                      onClick={() => downloadBase64File(resultData.excel_base64, `${resultData.circuit_type}_bom_spec.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 rounded-lg text-xs font-semibold transition"
                    >
                      <Download className="w-4 h-4 text-emerald-400" />
                      {isUk ? 'Завантажити BOM (.xlsx)' : isPl ? 'Pobierz BOM (.xlsx)' : 'Download BOM (.xlsx)'}
                    </button>
                  )}

                  {resultData.docx_base64 && (
                    <button
                      onClick={() => downloadBase64File(resultData.docx_base64, `${resultData.circuit_type}_engineering_report.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-lg text-xs font-semibold transition"
                    >
                      <FileText className="w-4 h-4 text-blue-400" />
                      {isUk ? 'Звіт ДСТУ (.docx)' : isPl ? 'Raport Word (.docx)' : 'Full Report (.docx)'}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(resultData.markdown || '');
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 2500);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-xs font-medium transition"
                  >
                    {isCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    {isCopied ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Скопіювати' : 'Copy')}
                  </button>

                  <button
                    onClick={() => setShowSignModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 rounded-lg text-xs font-semibold transition"
                  >
                    <ShieldCheck className="w-4 h-4 text-indigo-400" />
                    {isUk ? 'КЕП / ЕЦП Підпис' : isPl ? 'Podpis Kwalifikowany' : 'Digital Sign'}
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto rounded-xl border border-slate-800">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead className="bg-slate-950 text-xs font-semibold text-slate-400 uppercase border-b border-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-center">№</th>
                      <th className="px-4 py-3">{isUk ? 'Поз.' : 'Ref'}</th>
                      <th className="px-4 py-3">{isUk ? 'Найменування' : 'Item Name'}</th>
                      <th className="px-4 py-3">{isUk ? 'Розрахований номінал' : 'Rating / Value'}</th>
                      <th className="px-4 py-3">{isUk ? 'Корпус / Монтаж' : 'Package / Footprint'}</th>
                      <th className="px-4 py-3 text-center">{isUk ? 'К-ть' : 'Qty'}</th>
                      <th className="px-4 py-3">{isUk ? 'Виробник / MPN' : 'MPN / Note'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 font-mono text-xs">
                    {resultData.bom && resultData.bom.map((item: any) => (
                      <tr key={item.pos} className="hover:bg-slate-800/40 transition">
                        <td className="px-4 py-2.5 text-center text-slate-500">{item.pos}</td>
                        <td className="px-4 py-2.5 font-bold text-amber-400">{item.ref}</td>
                        <td className="px-4 py-2.5 font-sans font-medium text-slate-200">{item.name}</td>
                        <td className="px-4 py-2.5 text-emerald-300">{item.value}</td>
                        <td className="px-4 py-2.5 text-slate-400">{item.package}</td>
                        <td className="px-4 py-2.5 text-center font-bold text-white bg-slate-950/40">{item.qty} шт</td>
                        <td className="px-4 py-2.5 text-slate-400 font-sans">{item.mpn || item.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Digital Signature Modal */}
        {showSignModal && (
          <DigitalSignatureModal
            isOpen={showSignModal}
            onClose={() => setShowSignModal(false)}
            reportContent={resultData?.markdown || ''}
            language={language}
          />
        )}
      </div>
    </div>
  );
}
